import { createStore, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { composeWithDevTools } from 'redux-devtools-extension';
import rootReducer from './RootReducer';
import rootSaga from './RootSaga';
import { GUEST_CONTEXT_SESSION_KEY } from '../constants/AppConstants';

/**
 * Reads the persisted GuestAccess context from sessionStorage and, if present,
 * returns it as the preloaded state for the GuestAccess Redux slice.
 *
 * Why here?
 * ─────────
 * Full browser navigations (window.location.assign / Okta redirect / magic-link)
 * destroy the JS runtime and reset the Redux store.  SharedStartupGate writes the
 * guest context to sessionStorage BEFORE any such navigation so this function can
 * restore it the moment the store is created — before any component renders.
 *
 * The key is consumed (removed) immediately so it is read exactly once, preventing
 * stale context from leaking across unrelated page visits.
 *
 * #genai
 */
const consumeGuestContextFromSession = () => {
  try {
    const raw = sessionStorage.getItem(GUEST_CONTEXT_SESSION_KEY);
    if (!raw) return undefined;
    sessionStorage.removeItem(GUEST_CONTEXT_SESSION_KEY);
    return { GuestAccess: JSON.parse(raw) };
  } catch (_) {
    return undefined;
  }
};

const configureStore = () => {
  const sagaMiddleware = createSagaMiddleware();
  const preloadedState = consumeGuestContextFromSession();
  return {
    ...createStore(rootReducer, preloadedState, composeWithDevTools(applyMiddleware(sagaMiddleware))),
    runSaga: sagaMiddleware.run(rootSaga),
  };
};

export const store = configureStore();

export default configureStore;
